<script lang="ts">
const AGREEMENT_EDITABLE_STATUSES = new Set<AgreementStatus>([
  AgreementStatus.ACTIVE,
  AgreementStatus.DRAFT,
]);
</script>

<!-- eslint-disable @typescript-eslint/unified-signatures -->

<script setup lang="ts">
import {
  Agreement,
  AgreementStatus,
  ApproveAgreementRequest,
  ApproveAgreementRequestSchema,
  CollateralType,
  Currency,
  Data,
  SettlementSystem,
  SettlementType,
  availableSettlementSystems,
  create,
  formatCollateralMethod,
  formatCollateralType,
  formatCurrency,
  formatDeliveryType,
  formatProfitCenter,
  formatRoundingRule,
  formatSettlementSystem,
  formatSettlementType,
  formatSide,
  formatTermType,
  getAgreementStatusName,
} from '@/connect';
import { useIsIncoming } from '@/modules/agreements/composables';
import { Permission, useHasPermission } from '@/composables/user-permissions';
import {
  formatDateTime,
  formatDecimal,
  formatPercentCollateral,
  formatString,
  formatTimestamp,
} from '@/modules/agreements/utils';
import { useRules } from '@/composables/vuetify-validation';
import { computed, ref, shallowRef, watch, watchEffect } from 'vue';
import CompanyInfoDisplay from '@/modules/user-accounts/components/CompanyInfoDisplay.vue';
import NumericInput from '@/modules/common/components/NumericInput.vue';
import Decimal from 'decimal.js';

const props = withDefaults(
  defineProps<{
    agreement: Data<Agreement>;
    reservedShortNames?: Set<string>;
  }>(),
  {
    reservedShortNames: () => new Set(),
  }
);

const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'close', agreement: Data<Agreement>): void;
  (event: 'duplicate', agreement: Data<Agreement>): void;
  (event: 'edit', agreement: Data<Agreement>): void;
  (event: 'reject', agreement: Data<Agreement>, onRejected: () => void): void;
  (event: 'approve', request: Data<ApproveAgreementRequest>): void;
}>();

const form = ref<{ resetValidation(): void } | null>(null);

const canUserEditAgreement = useHasPermission(Permission.AGREEMENT_EDIT);
const isIncoming = useIsIncoming(() => props.agreement);
const isIncomingDraft = computed(
  () => props.agreement.status === AgreementStatus.DRAFT && isIncoming.value
);
const isAgreementEditable = computed(() => AGREEMENT_EDITABLE_STATUSES.has(props.agreement.status));

const shortNameInitial = shallowRef('');
watch(
  () => props.agreement,
  () => (shortNameInitial.value = props.agreement.shortName),
  { immediate: true }
);

const shortName = shallowRef('');
watchEffect(() => (shortName.value = props.agreement.shortName));

const balanceTarget = shallowRef(new Decimal(0));
watch(
  () => props.agreement,
  () => (balanceTarget.value = new Decimal(props.agreement.balanceTarget || 0.0)),
  { immediate: true }
);

const balanceTargetVariance = shallowRef(new Decimal(0));
watch(
  () => props.agreement,
  () => (balanceTargetVariance.value = new Decimal(props.agreement.balanceTargetVariance || 0.0)),
  { immediate: true }
);

const bookingAccount = shallowRef<string | undefined>('');
watch(
  () => props.agreement,
  () => (bookingAccount.value = props.agreement.bookingAccount || undefined),
  { immediate: true }
);

const profitCenter = shallowRef<string | undefined>('');
watch(
  () => props.agreement,
  () => (profitCenter.value = props.agreement.profitCenter || undefined),
  { immediate: true }
);

const settlementSystem = shallowRef(SettlementSystem.SETTLEMENT_SYSTEM_UNSPECIFIED);
watchEffect(
  () =>
    (settlementSystem.value =
      props.agreement.settlementType === SettlementType.NSCC
        ? SettlementSystem.PROVABLE_MARKET
        : (props.agreement.settlementSystem ?? SettlementSystem.SETTLEMENT_SYSTEM_UNSPECIFIED))
);

const isSettlementTypeNSCC = computed(() => props.agreement.settlementType === SettlementType.NSCC);

const settlementSystemOptions = computed(() =>
  availableSettlementSystems.map((settlementSystem) => ({
    ...settlementSystem,
    disabled:
      isSettlementTypeNSCC.value && settlementSystem.id !== SettlementSystem.PROVABLE_MARKET,
  }))
);

function isValueValidAndReadonly<T>(value: T, target: T) {
  if (!isSettlementTypeNSCC.value) return false;
  return value === target;
}

const { rules, validateAndSubmit } = useRules<{
  shortName: string;
  bookingAccount: string;
  tradeType: string;
  profitCenter: string;
}>({
  shortName: {
    rules: [
      (value) => !!value || 'Required',
      (value) =>
        value === shortNameInitial.value ||
        !props.reservedShortNames.has(value.trim().toUpperCase()) ||
        'Must be unique',
      (value) => value.length <= 25 || 'Must have at most 25 characters',
      (value) => !/['",]/.test(value) || 'Commas and quotes are not allowed',
    ],
  },
  bookingAccount: {
    rules: [(value) => !value || value.length <= 20 || 'Must have at most 20 characters'],
  },
  tradeType: {
    rules: [(value) => !value || value.length <= 30 || 'Must have at most 30 characters'],
  },
  profitCenter: {
    rules: [(value) => !value || value.length <= 4 || 'Must have at most 4 characters'],
  },
});

function closeDialog() {
  emit('cancel');
}

function agreementClose(agreement: Data<Agreement>) {
  emit('close', agreement);
  closeDialog();
}

function agreementDuplicate(agreement: Data<Agreement>) {
  emit('duplicate', agreement);
  closeDialog();
}

function agreementEdit(agreement: Data<Agreement>) {
  emit('edit', agreement);
  closeDialog();
}

function agreementReject(agreement: Data<Agreement>) {
  form.value?.resetValidation();
  emit('reject', agreement, closeDialog);
}

function agreementApprove(agreement: Data<Agreement>) {
  emit(
    'approve',
    create(ApproveAgreementRequestSchema, {
      id: agreement.id,
      shortName: shortName.value,
      settlementSystem: settlementSystem.value || undefined,
      balanceTarget: balanceTarget.value.toString() || undefined,
      balanceTargetVariance: balanceTargetVariance.value.toString() || undefined,
      bookingAccount: bookingAccount.value || undefined,
      profitCenter: profitCenter.value || undefined,
    })
  );
  closeDialog();
}
</script>

<template>
  <VDialog
    v-shortkey="['esc']"
    content-class="au-popup-dialog"
    max-width="800"
    overlay-color="secondary"
    overlay-opacity="0.80"
    persistent
    scrollable
    :value="true"
    @click:outside="closeDialog"
    @keydown.esc="closeDialog"
    @shortkey.native="closeDialog"
  >
    <VForm ref="form" novalidate @submit.prevent>
      <VCard>
        <VBtn class="close-icon" icon @click="closeDialog">
          <VIcon>mdi-close</VIcon>
        </VBtn>
        <VCardTitle class="d-flex align-center flex-grow-1">
          <span class="headline">
            {{ getAgreementStatusName(agreement.status, isIncoming) }} Agreement
          </span>
          <span class="text--primary headline-2 ml-auto">
            {{ agreement.id }}
          </span>
        </VCardTitle>
        <div class="form-grid">
          <div class="col-span-2 form-group" label="Approved DateTime">
            <VTextField label="Side" readonly tabindex="-1" :value="formatSide(agreement.side)" />
            <CompanyInfoDisplay
              data-test-id="agreement-counterparty-id"
              :display-id="agreement.counterpartyId"
              label="Counterparty"
              readonly
              tabindex="-1"
            />
            <VTextField
              data-test-id="agreement-settlement-typ"
              label="Settlement Type"
              readonly
              :value="formatSettlementType(agreement.settlementType)"
            />
            <VTextField
              v-bind="rules.shortName"
              :autofocus="isIncomingDraft"
              class="col-span-3"
              :data-required="isIncomingDraft"
              data-test-id="agreement-short-name"
              hint="Note: Short Name is only visible to you and will not be shared with your counterparty"
              label="Agreement Short Name"
              :readonly="!isIncomingDraft"
              :tabindex="!isIncomingDraft ? -1 : undefined"
              :value="agreement.shortName"
              @input="(value) => (shortName = value)"
            />
            <VTextField
              v-bind="rules.bookingAccount"
              auto-grow
              data-test-id="agreement-booking-account"
              label="Booking Account"
              :readonly="!isIncomingDraft"
              :tabindex="!isIncomingDraft ? -1 : undefined"
              :value="agreement.bookingAccount ?? '–'"
              @input="(value) => (bookingAccount = value)"
            />
            <VTextField
              data-test-id="agreement-counterparty-booking-account"
              label="Counterparty Booking Account"
              readonly
              tabindex="-1"
              :value="agreement.counterpartyBookingAccount ?? '–'"
            />
            <VTextField
              data-test-id="agreement-trade-type"
              v-bind="rules.tradeType"
              label="Trade Type"
              readonly
              tabindex="-1"
              :value="agreement.tradeType ?? '–'"
            />
          </div>

          <span class="grid-subtitle">Collateral</span>
          <div class="form-group">
            <VTextField
              data-test-id="agreement-collateral-type"
              label="Collateral Type"
              readonly
              tabindex="-1"
              :value="formatCollateralType(agreement.collateralType)"
            />
            <VTextField
              data-test-id="agreement-pricing-currency"
              label="Pricing Currency"
              readonly
              tabindex="-1"
              :value="formatCurrency(agreement.pricingCurrency)"
            />
            <VTextField
              data-test-id="agreement-collateral"
              label="Collateral Rate"
              readonly
              tabindex="-1"
              :value="formatPercentCollateral(agreement.collateralRate)"
            />
            <VTextField
              v-if="agreement.collateralType == CollateralType.CASH"
              data-test-id="agreement-collateral-currency"
              label="Collateral Currency"
              readonly
              tabindex="-1"
              :value="formatCurrency(agreement.collateralCurrency || Currency.UNSPECIFIED_CURRENCY)"
            />
            <VTextField
              data-test-id="agreement-delivery-type"
              label="Delivery Type"
              readonly
              tabindex="-1"
              :value="formatDeliveryType(agreement.deliveryType)"
            />
            <VTextField
              data-test-id="agreement-collateral-method"
              label="Collateral Method"
              readonly
              tabindex="-1"
              :value="formatCollateralMethod(agreement.collateralMethod)"
            />
          </div>

          <span class="grid-subtitle">Billing and Dividends</span>
          <div class="form-group">
            <VTextField
              data-test-id="agreement-dividend-required"
              label="Dividend Required"
              :precision="2"
              readonly
              suffix="%"
              tabindex="-1"
              type="decimal"
              :value="agreement.dividendRequired"
            />
            <VTextField
              data-test-id="agreement-billing-currency"
              label="Billing Currency"
              readonly
              tabindex="-1"
              :value="formatCurrency(agreement.billingCurrency)"
            />
            <VTextField
              data-test-id="agreement-price-rounding-rule"
              label="Price Rounding Rules"
              readonly
              tabindex="-1"
              :value="formatRoundingRule(agreement.roundingRule)"
            />
          </div>

          <span class="grid-subtitle">Term</span>
          <div class="form-group">
            <VTextField
              data-test-id="agreement-term-type"
              label="Term Type"
              readonly
              tabindex="-1"
              :value="formatTermType(agreement.termType)"
            />
            <VTextField
              data-test-id="agreement-term-start-date"
              label="Term Start Date"
              readonly
              tabindex="-1"
              :value="formatDateTime(agreement.termStartDate)"
            />
            <VTextField
              data-test-id="agreement-term-end-date"
              label="Term End Date"
              readonly
              tabindex="-1"
              :value="formatDateTime(agreement.termEndDate)"
            />
          </div>

          <span class="grid-subtitle">Balance Targets</span>
          <div class="form-group">
            <NumericInput
              v-if="isIncomingDraft"
              v-model="balanceTarget"
              data-test-id="agreement-balance-target-input"
              label="Balance Target"
              :min="0"
              :precision="2"
              type="decimal"
            />
            <VTextField
              v-else
              data-test-id="agreement-balance-target-field"
              label="Balance Target"
              readonly
              tabindex="-1"
              :value="formatDecimal(agreement.balanceTarget)"
            />
            <NumericInput
              v-if="isIncomingDraft"
              v-model="balanceTargetVariance"
              data-test-id="agreement-balance-target-variance-input"
              label="Balance Target Variance"
              :min="0"
              :precision="2"
              suffix="%"
              type="decimal"
            />
            <VTextField
              v-else
              data-test-id="agreement-balance-target-variance-field"
              label="Balance Target Variance"
              readonly
              suffix="%"
              tabindex="-1"
              :value="formatDecimal(agreement.balanceTargetVariance)"
            />
          </div>

          <span class="grid-subtitle">Misc</span>
          <div class="form-group">
            <VTextField
              data-test-id="agreement-rwa-bucket"
              label="RWA Bucket"
              readonly
              tabindex="-1"
              :value="formatString(agreement.rwaBucket)"
            />
            <VSelect
              v-if="isIncomingDraft"
              v-model="settlementSystem"
              data-test-id="agreement-settlement-system-select"
              item-text="name"
              item-value="id"
              :items="settlementSystemOptions"
              label="Settlement System"
              :readonly="
                isValueValidAndReadonly(settlementSystem, SettlementSystem.PROVABLE_MARKET)
              "
              tabindex="-1"
            />
            <VTextField
              v-else
              data-test-id="agreement-settlement-system-field"
              label="Settlement System"
              readonly
              tabindex="-1"
              :value="formatSettlementSystem(agreement.settlementSystem)"
            />
            <VTextField
              v-if="isIncomingDraft"
              v-model="profitCenter"
              v-bind="rules.profitCenter"
              data-test-id="agreement-profit-center"
              label="Profit Center"
              tabindex="-1"
            />
            <VTextField
              v-else
              data-test-id="agreement-profit-center"
              label="Profit Center"
              readonly
              tabindex="-1"
              :value="formatProfitCenter(profitCenter)"
            />
          </div>

          <span class="grid-subtitle">Created</span>
          <div class="form-group">
            <VTextField
              class="col-start-1 col-span-2"
              data-test-id="agreement-created-by"
              label="Created By"
              readonly
              tabindex="-1"
              :value="agreement.proposerUser"
            />
            <VTextField
              data-test-id="agreement-created-at"
              label="Created At"
              readonly
              tabindex="-1"
              :value="formatTimestamp(agreement.createdAt)"
            />
          </div>

          <template v-if="agreement.responderUser">
            <span class="grid-subtitle">Approved</span>
            <div class="form-group">
              <VTextField
                class="col-start-1 col-span-2"
                data-test-id="agreement-approved-by"
                label="Approved By"
                readonly
                tabindex="-1"
                :value="agreement.responderUser"
              />
              <VTextField
                data-test-id="agreement-approved-at"
                label="Approved At"
                readonly
                tabindex="-1"
                :value="formatTimestamp(agreement.approvedAt)"
              />
            </div>
          </template>
        </div>
        <VCardActions>
          <VBtn tabindex="-1" @click="closeDialog">Cancel</VBtn>
          <VSpacer />
          <template v-if="isIncomingDraft && canUserEditAgreement">
            <VBtn color="red" @click="agreementReject(agreement)">
              <VIcon left>mdi-close</VIcon>
              Reject
            </VBtn>
            <VBtn color="green" @click="validateAndSubmit(() => agreementApprove(agreement))">
              <VIcon left>mdi-check</VIcon>
              Approve
            </VBtn>
          </template>
          <template v-else-if="canUserEditAgreement">
            <VBtn v-if="isAgreementEditable" @click="agreementClose(agreement)">
              <VIcon left>mdi-close</VIcon>
              Close
            </VBtn>
            <VBtn @click="agreementDuplicate(agreement)">
              <VIcon left>mdi-plus-circle-multiple-outline</VIcon>
              Duplicate
            </VBtn>
            <VBtn v-if="isAgreementEditable" @click="agreementEdit(agreement)">
              <VIcon left>mdi-pencil</VIcon>
              Edit
            </VBtn>
          </template>
        </VCardActions>
      </VCard>
    </VForm>
  </VDialog>
</template>

<style lang="scss" scoped>
.close-icon {
  top: 0.4rem;
  right: 0.4rem;
  position: absolute;
}

.form-grid {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1rem;
  padding: 0 2rem 1rem;
  margin-top: 1rem;
  overflow-y: auto;

  & > * {
    margin-top: 0;
  }

  & > {
    .grid-subtitle {
      color: #6fedc7;
      grid-column: 1;
    }
    .grid-subtitle + .form-group {
      grid-column: 2;
    }
    .form-group {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 1rem;

      ::v-deep {
        // Error messages occupy more space on bottom, so adjusting their height accordingly
        .v-text-field__details {
          interpolate-size: allow-keywords;

          transition:
            height ease-in-out 300ms,
            min-height ease-in-out 300ms;
          overflow: clip;

          &:not(:has(.v-messages__wrapper:empty)) {
            height: auto;
            min-height: 28px;
          }
          &:has(.v-messages__wrapper:empty) {
            min-height: 0;
            height: 0;
          }
        }
      }
    }
  }
}

.col-start-1 {
  grid-column-start: 1;
}
.col-span-2 {
  grid-column: span 2 / span 2;
}
.col-span-3 {
  grid-column: span 3 / span 3;
}
</style>
